import type { Json, JsonObject } from "../json";
import type { TranscriptMessage } from "../models";

/** 体验模式的示例数据（和 iOS 的 DemoContent 一致）。内容都是虚构的，只为展示界面能做什么。 */
export interface DemoSession {
  storedID: string;
  profile: string;
  title?: string;
  /** Unix 秒 */
  startedAt: number;
  messages: TranscriptMessage[];
  liveID?: string;
}

export const cronRunSession = "demo-cron-news";

export const demoProfiles: JsonObject[] = [
  { name: "default", is_default: true, model: "示例模型", provider: "demo", skill_count: 6,
    description: "日常助理：查资料、整理文档、安排日程", ui_meta: { "hermes-bots": { title: "小助理" } } },
  { name: "writer", is_default: false, model: "示例模型", provider: "demo", skill_count: 3,
    description: "写文案、润色、翻译", ui_meta: { "hermes-bots": { title: "写作助手" } } },
  { name: "researcher", is_default: false, model: "示例模型", provider: "demo", skill_count: 4,
    description: "读论文、做行业调研、写报告", ui_meta: { "hermes-bots": { title: "研究员" } } },
];

export function demoSoul(name: string): string {
  switch (name) {
    case "writer": return "你是一位文字功底扎实的写作助手，语气自然，不堆砌形容词。";
    case "researcher": return "你是一位严谨的研究员，结论要有出处，不确定的地方直接说明。";
    default: return "你是一位贴心、高效的个人助理，回答简洁，先给结论。";
  }
}

export const demoUsage: JsonObject = {
  model: "示例模型", input: 1820, output: 640, total: 2460, calls: 2,
  context_used: 2460, context_max: 131072, context_percent: 2,
};

function dedent(text: string): string {
  const lines = text.replace(/^\n/, "").split("\n");
  const indents = lines.filter((l) => l.trim()).map((l) => l.match(/^ */)![0].length);
  const min = indents.length ? Math.min(...indents) : 0;
  return lines.map((l) => l.slice(min)).join("\n").replace(/\s+$/, "");
}

export function demoSessions(): DemoSession[] {
  const now = Date.now() / 1000;
  const msg = (role: string, text: string, t: number, name?: string, context?: string): TranscriptMessage =>
    ({ role, text, timestamp: t, name, context });
  const today8 = new Date();
  today8.setHours(8, 0, 0, 0);
  const t8 = today8.getTime() / 1000;
  const newsAt = t8 < now ? t8 : t8 - 86400;
  return [
    { storedID: "demo-weekly", profile: "default", title: "整理本周工作周报", startedAt: now - 2 * 3600, messages: [
      msg("user", "帮我把这周的会议记录整理成周报，重点写进展和下周计划", now - 2 * 3600),
      msg("tool", "", now - 2 * 3600 + 3, "read_file", "notes/2026-W41-meetings.md"),
      msg("assistant", dedent(`
        **本周周报（第 41 周）**

        **主要进展**
        - 新版 App 完成内测，修复了 12 个问题，启动速度提升约 40%
        - 和设计团队确定了 Bot 分享页的交互，下周开始开发
        - 客户访谈 3 场，收集到的需求已整理进需求池

        **风险与问题**
        - 推送服务依赖的证书还在申请中，可能影响下周上线时间

        **下周计划**
        1. 开发 Bot 分享页
        2. 接入系统推送
        3. 准备公开测试

        需要我按你们公司的周报模板调整格式吗？
      `), now - 2 * 3600 + 9),
    ] },
    { storedID: cronRunSession, profile: "default", title: "早间新闻摘要", startedAt: newsAt, messages: [
      msg("user", "整理今天值得关注的科技新闻，每条一句话，最后给一句总结", newsAt),
      msg("tool", "", newsAt + 4, "web_search", "今日 科技 新闻"),
      msg("assistant", dedent(`
        **今日科技要点**

        1. 多家手机厂商发布新一代折叠屏，价格首次下探到四千元档
        2. 一款开源大模型在代码评测上追平主流商业模型
        3. 国内某城市开放全域自动驾驶出租车试运营
        4. 卫星互联网服务新增覆盖东南亚三国

        **一句话总结**：端侧 AI 和开源模型仍是本周最热的方向。

        *（这是定时任务每天早上 8 点自动运行的结果）*
      `), newsAt + 48),
    ] },
    { storedID: "demo-seoul", profile: "default", title: "首尔三日游攻略", startedAt: now - 26 * 3600, messages: [
      msg("user", "下个月去首尔玩三天，喜欢逛街和吃，帮我排个行程", now - 26 * 3600),
      msg("assistant", dedent(`
        好的，按“逛街 + 美食”给你排了一个不赶的三天：

        | 天 | 上午 | 下午 | 晚上 |
        |---|---|---|---|
        | 第一天 | 景福宫（穿韩服免门票） | 北村韩屋村、三清洞咖啡 | 广藏市场吃绿豆饼 |
        | 第二天 | 圣水洞买手店 | 首尔林散步 | 弘大街头表演、烤肉 |
        | 第三天 | 明洞购物 | 南山塔看夜景前的日落 | 汉江边炸鸡 + 啤酒 |

        **小提示**
        - 交通用 T-money 卡，地铁和便利店都能用
        - 圣水洞很多店 11 点才开门，不用起太早
        - 想去的热门餐厅提前在 App 上取号

        要我把这份行程做成日历提醒吗？
      `), now - 26 * 3600 + 12),
    ] },
    { storedID: "demo-mcp", profile: "default", title: "什么是 MCP", startedAt: now - 3 * 86400, messages: [
      msg("user", "用大白话解释一下 MCP 是什么", now - 3 * 86400),
      msg("assistant", dedent(`
        **MCP（模型上下文协议）就像 AI 的“万能插座”。**

        以前每个 AI 应用要接入一个外部工具（比如日历、数据库、浏览器），都得单独写一套对接代码。
        MCP 规定了一个统一的接口：工具按这个标准做一次，所有支持 MCP 的 AI 都能直接用。

        举个例子：装上一个“日历 MCP”，你就可以直接对我说“下周三下午帮我约个会”，我会自己去查空闲时间并创建日程。
      `), now - 3 * 86400 + 8),
    ] },
    { storedID: "demo-coffee", profile: "writer", title: "秋季咖啡新品文案", startedAt: now - 5 * 3600, messages: [
      msg("user", "给我们店的秋季新品“桂花拿铁”写一段小红书文案，轻松一点", now - 5 * 3600),
      msg("assistant", dedent(`
        🍂 **秋天的第一杯，是桂花味的**

        推开门就闻到一阵桂花香，以为是窗外飘进来的——
        原来是今年的新品 **桂花拿铁** 🌼

        浓缩打底，加一勺自己熬的桂花蜜，奶泡上再撒一点干桂花，
        第一口是花香，第二口是咖啡，喝完嘴里还是甜甜的秋天。

        📍 限时到 11 月底，第二杯半价
        #秋天的第一杯咖啡 #桂花拿铁 #周末去哪儿
      `), now - 5 * 3600 + 10),
    ] },
    { storedID: "demo-paper", profile: "researcher", title: "论文速读：长上下文", startedAt: now - 2 * 86400, messages: [
      msg("user", "帮我速读一篇关于长上下文的论文，告诉我它解决了什么问题", now - 2 * 86400),
      msg("tool", "", now - 2 * 86400 + 3, "web_extract", "arxiv.org/abs/示例论文"),
      msg("assistant", dedent(`
        **一句话**：这篇论文让模型在读超长文档时，不再“读了后面忘了前面”。

        - **问题**：上下文越长，模型对中间部分的信息越容易遗漏
        - **方法**：把长文档分块做摘要索引，回答时先查索引再精读相关段落
        - **结果**：在 20 万字的问答测试里，准确率提升明显，耗时只增加一点

        需要我把方法部分画成流程图吗？
      `), now - 2 * 86400 + 15),
    ] },
  ];
}

export interface DemoReply {
  title: string;
  thinking?: string;
  tool?: { name: string; context: string; summary: string };
  text: string;
}

export function demoReply(input: string): DemoReply {
  const text = input.trim();
  const lower = text.toLowerCase();
  const has = (...words: string[]) => words.some((w) => lower.includes(w));

  if (has("天气", "weather")) {
    return {
      title: "查询天气", thinking: "用户想知道天气，先搜索一下最新的预报。",
      tool: { name: "web_search", context: "今天 天气 预报", summary: "找到 5 条结果" },
      text: dedent(`
        **今明两天（示例数据）**：多云转晴，18～25°C，东南风 2 级，空气质量良。

        - 早晚温差大，出门带一件薄外套
        - 紫外线中等，中午外出注意防晒

        *体验模式里的天气是示例数据。连接你自己的 Hermes 后，它会真的去查实时天气。*
      `),
    };
  }
  if (has("你好", "您好", "hello", "hi", "嗨", "在吗")) {
    return {
      title: "打个招呼",
      text: dedent(`
        你好！我是 TimiBot 体验模式里的示例助理 👋

        TimiBot 是 Hermes Agent 的客户端。连上你自己电脑或服务器上的 Hermes 后，你可以：

        - **随时对话**：用你自己选的模型，回答都留在你自己的机器上
        - **让它动手做事**：搜索网页、读写文件、运行命令（危险操作会先问你）
        - **定时任务**：比如每天早上自动整理新闻，做完提醒你
        - **多个 Bot**：写作、研究、编程，各自有自己的设定和技能
        - **手机、电脑都能用**：iPhone App、网页版用的是同一套对话

        随便问我点什么试试看吧。
      `),
    };
  }
  if (has("写", "文案", "邮件", "润色", "翻译")) {
    return {
      title: "写作示例", thinking: "先确认用途和语气，再给一个可以直接用的版本。",
      text: dedent(`
        好的，先给你一个可以直接用的版本：

        > 你好，感谢你一直以来的支持！我们下周会上线一个新功能，届时会第一时间告诉你。如果有任何问题，随时回复这封邮件就好。

        需要更正式、更活泼，或者翻译成英文吗？

        *体验模式里的回复是预先写好的示例。连接你自己的 Hermes 后，会由你配置的模型根据你的要求来写。*
      `),
    };
  }
  const chars = [...text];
  const quoted = chars.length > 60 ? chars.slice(0, 60).join("") + "…" : text;
  const title = chars.slice(0, 14).join("") || "新对话";
  return {
    title,
    thinking: "这是体验模式，没有连接真实的模型，给出一段说明。",
    tool: { name: "web_search", context: quoted, summary: "示例结果" },
    text: dedent(`
      收到：「${quoted}」

      这里是**体验模式**，回复是预先写好的示例，不会真的调用模型。

      连接你自己的 Hermes Agent 之后，这条消息会发到你自己的电脑或服务器上，由你配置的模型来回答；
      它还能搜索网页、读写文件、运行定时任务，做完了提醒你。

      想连接自己的 Hermes：设置 → 连接 → 添加连接，按提示在主机上运行一条命令、输入配对码即可。
    `),
  };
}

function isoDate(daysFromNow: number, hour: number): string {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  d.setHours(hour, 0, 0, 0);
  return d.toISOString();
}

export function demoCronJobs(): JsonObject[] {
  return [
    { job_id: "demo-news", name: "早间新闻摘要", schedule: "0 8 * * *", deliver: "local", enabled: true,
      prompt_preview: "整理今天值得关注的科技新闻，每条一句话", last_status: "ok",
      next_run_at: isoDate(1, 8), last_run_at: isoDate(0, 8) },
    { job_id: "demo-weekly", name: "周一周报提醒", schedule: "0 9 * * 1", deliver: "local", enabled: true,
      prompt_preview: "提醒我写周报，并列出上周的会议记录", last_status: "ok", next_run_at: isoDate(3, 9) },
    { job_id: "demo-todo", name: "下班前整理待办", schedule: "30 18 * * 1-5", deliver: "local", enabled: false,
      state: "paused", prompt_preview: "把今天没做完的事整理成明天的待办清单" },
  ];
}

export const demoSkills: JsonObject[] = [
  { name: "web-research", description: "多来源搜索并整理成带出处的摘要", category: "研究", enabled: true, usage: 28, provenance: "bundled" },
  { name: "arxiv", description: "搜索和速读 arXiv 论文", category: "研究", enabled: true, usage: 9, provenance: "hub" },
  { name: "weekly-report", description: "把会议记录整理成周报", category: "办公", enabled: true, usage: 6, provenance: "agent" },
  { name: "xlsx", description: "读写 Excel 表格、做数据透视", category: "办公", enabled: true, usage: 4, provenance: "bundled" },
  { name: "travel-planner", description: "按兴趣和预算规划行程", category: "生活", enabled: true, usage: 2, provenance: "hub" },
  { name: "xiaohongshu-copy", description: "写小红书风格的种草文案", category: "写作", enabled: false, usage: 0, provenance: "hub" },
];

export const demoSkillTree: JsonObject = {
  "研究": ["web-research", "arxiv"], "办公": ["weekly-report", "xlsx"], "生活": ["travel-planner"], "写作": ["xiaohongshu-copy"],
};

export const demoHubItems: JsonObject[] = [
  { name: "pdf", description: "读取、合并、拆分 PDF", source: "official", trust: "official", identifier: "demo/pdf" },
  { name: "calendar", description: "查看和创建日程", source: "official", trust: "official", identifier: "demo/calendar" },
  { name: "stock-watch", description: "关注股票价格变动并提醒", source: "community", trust: "community", identifier: "demo/stock-watch" },
];

export const demoCommands: Json = [
  ["/new", "开始新对话"], ["/model", "切换模型"], ["/goal", "设定一个长期目标，让 Bot 持续推进"],
  ["/background", "把任务放到后台执行，做完通知你"], ["/btw", "不打断当前任务，问个旁支问题"], ["/usage", "查看本次对话用量"],
];
